<template>
    <div class="linked-patient-details">
        <div class="d-flex align-items-center">
            <b-button
                v-b-toggle="collapseId"
                variant="link"
                size="sm"
                class="p-0 text-decoration-none chevron-toggle mr-2"
                :title="expanded ? 'Hide details' : 'Show details'"
                :aria-label="expanded ? 'Hide details' : 'Show details'"
            >
                <i class="fa fa-chevron-right toggle-icon" :class="{ 'toggle-icon-open': expanded }" />
            </b-button>
            <div class="flex-grow-1"><slot /></div>
        </div>
        <b-collapse
            :id="collapseId"
            :visible="defaultExpanded"
            @show="onShow"
            @input="expanded = $event"
        >
            <div class="border rounded p-2 mt-1 bg-light">
                <div class="mb-1">
                    <strong>Phone:</strong>
                    <span v-if="patient.phoneDisplay" class="ml-1">{{ patient.phoneDisplay }}</span>
                    <span v-else class="ml-1 text-muted font-italic">No phone on file</span>
                </div>
                <div class="mb-2">
                    <strong>Cell:</strong>
                    <span v-if="patient.cellDisplay" class="ml-1">{{ patient.cellDisplay }}</span>
                    <span v-else class="ml-1 text-muted font-italic">No cell on file</span>
                </div>

                <div>
                    <strong>Acceptance Records:</strong>
                    <div v-if="loading" class="text-muted">
                        <b-spinner small class="mr-1" />
                        Loading...
                    </div>
                    <div v-else-if="hasError" class="text-danger">
                        Error loading acceptance records.
                    </div>
                    <div v-else-if="!records.length" class="text-muted font-italic">
                        No acceptance records on file
                    </div>
                    <ul v-else class="mb-0 pl-4">
                        <li v-for="record in records" :key="record.id">
                            {{ acceptanceName(record) }}
                            <b-badge v-if="record.declined" variant="danger" class="ml-1">Declined</b-badge>
                            <b-badge v-else-if="record.signatureData" variant="success" class="ml-1">Signed</b-badge>
                            <b-badge v-else variant="secondary" class="ml-1">Accepted</b-badge>
                            <span class="text-muted ml-1">{{ record.created | formatDate("L") }}</span>
                        </li>
                    </ul>
                </div>
            </div>
        </b-collapse>
    </div>
</template>

<script lang="ts">
import { Component, Vue, Prop } from "vue-property-decorator";
import { Patient } from "@/models/Patient";
import { AcceptanceRecord } from "@/models/AcceptanceRecord";

@Component({ name: "LinkedPatientDetails" })
export default class LinkedPatientDetails extends Vue {
    @Prop({ required: true }) protected patient!: Patient;
    @Prop({ default: false }) protected defaultExpanded!: boolean;

    protected records: AcceptanceRecord[] = [];
    protected loading = false;
    protected hasError = false;
    protected loadedOnce = false;
    protected expanded = false;

    get collapseId(): string {
        return `linked-patient-details-${this.patient.id}`;
    }

    mounted() {
        if (this.defaultExpanded) {
            this.expanded = true;
            this.loadRecords();
        }
    }

    protected onShow() {
        if (!this.loadedOnce && !this.loading) {
            this.loadRecords();
        }
    }

    protected async loadRecords() {
        if (!this.patient?.id) return;
        try {
            this.loading = true;
            this.hasError = false;
            const resp = await this.$http.get<AcceptanceRecord[]>("AcceptanceRecord", {
                params: { patientId: this.patient.id },
            });
            this.records = resp.data ?? [];
            this.loadedOnce = true;
        } catch (err) {
            console.error("Error loading acceptance records for linked patient", err);
            this.hasError = true;
        } finally {
            this.loading = false;
        }
    }

    protected acceptanceName(record: AcceptanceRecord): string {
        return (record as any).acceptanceConfiguration?.name ?? `Record #${record.id}`;
    }
}
</script>

<style scoped>
.chevron-toggle {
    line-height: 1.5;
    border: none;
}
.chevron-toggle:hover,
.chevron-toggle:focus,
.chevron-toggle:active,
.chevron-toggle.focus {
    box-shadow: none !important;
    outline: none !important;
    border: none !important;
}
.toggle-icon {
    transition: transform 0.2s ease;
}
.toggle-icon-open {
    transform: rotate(90deg);
}
</style>
